import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FromLayout from "../../../component/FromLayout";
import {
  useGetParentPaymentQuery,
  useParentPaymentMutation,
} from "../../../store/api/parent/registrationSlice";
import { handleApiError } from "../../../utils/dateAndTimeConversion";

const initialState = {
  currency: "USD",
  paymentType: "full_payment",
};

const currency = ["USD", "CAD"];

const PaymentMethodPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(initialState);
  const [paymentInfo, setPaymentInfo] = useState(null);

  const { tempData, user } = useSelector((state) => state.UserReducer);
  const {
    data,
    isLoading: isGetLoading,
    isSuccess,
  } = useGetParentPaymentQuery();

  const [parentPayment, { isLoading }] = useParentPaymentMutation();

  const handlePaymentChange = (paymentType) => {
    if (paymentType === "partial_payment") {
      setFormData((prev) => ({
        ...prev,
        paymentType,
        partialAmount: paymentInfo.partial_payment,
      }));
    } else if (paymentType === "deposit") {
      const riderCount = (paymentInfo?.fee_breakdown ?? []).length || 1;
      setFormData((prev) => ({
        ...prev,
        paymentType,
        partialAmount: riderCount * 1000,
      }));
    } else {
      setFormData((prev) => ({ ...prev, paymentType }));
    }
  };

  const handleCurrencyChange = (currency) => {
    setFormData((prev) => ({ ...prev, currency }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast.dismiss();
    try {
      const studentIds = (paymentInfo?.fee_breakdown ?? [])
        .map((fb) => fb.student_id)
        .filter(Boolean);
      const response = await parentPayment({
        ...formData,
        isRegistration: true,
        ...(studentIds.length > 0 ? { studentIds } : {}),
      }).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        window.open(response.data.iframe_url, "_blank");
        navigate("/");
      }
    } catch (error) {
      handleApiError(error, t);
    }
  };

  useEffect(() => {
    if (isSuccess && data.data) {
      const summary = data.data?.summary;

      // Show due amount (not total) in case of partial payment
      const dueAmount = summary?.due_amount ?? summary?.total_amount;
      const displayAmount = String(dueAmount ?? "0").split(".")?.[0];

      // Build pro-rate summary from fee_breakdown
      const breakdown = summary?.fee_breakdown ?? [];
      const proRateInfo =
        breakdown.length > 0 && breakdown[0]?.pro_rate
          ? breakdown[0].pro_rate
          : null;

      const riderCount = breakdown.length || 1;
      const depositTotal = riderCount * 1000;
      setPaymentInfo({
        ...summary,
        total_amount: displayAmount,
        partial_payment: (parseFloat(displayAmount) / 3).toFixed(2),
        deposit_amount: depositTotal,
        deposit_per_rider: 1000,
        rider_count: riderCount,
        show_deposit: parseFloat(displayAmount) > depositTotal,
        pro_rate_info: proRateInfo,
        fee_breakdown: breakdown,
      });
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (!tempData) {
      navigate("/");
    }
  }, [tempData]);

  useEffect(() => {
    if (user?.is_private) {
      navigate("/dashboard");
    }
  }, [user]);

  return (
    <FromLayout
      title={`${t("parent")} & ${t("student")} ${t("registration")} ${t("fee")}`}
      width='w-75'
      maxWidth='1200px'
    >
      {/* Header */}
      <div className="text-center mb-5">
        <p
          className="text-light"
          style={{ opacity: 0.8, maxWidth: "500px", margin: "0 auto" }}
        >
          {t("choose_payment_option_note")}
        </p>
      </div>

      {/* Currency Selection */}
      <div className="row mb-4">
        <div className="col-12">
          <div
            className="currency-selection p-4 rounded"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <h6 className="text-white mb-3">
              {t("select")} {t("currency")}
            </h6>
            <div className="d-flex flex-wrap gap-3">
              {/* USD/CAD Radio Button */}
              {currency.map((c, i) => (
                <div
                  key={i}
                  className="currency-option d-flex align-items-center"
                  onClick={() => handleCurrencyChange(c)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="currency-radio me-2">
                    <div
                      className={`currency-circle ${
                        formData.currency === c ? "checked" : ""
                      }`}
                    ></div>
                  </div>
                  <span
                    className={`text-white ${
                      formData.currency === c ? "fw-semibold" : ""
                    }`}
                  >
                    {c === "USD" ? t("usd_currency") : t("cad_currency")}
                  </span>
                  {c === "USD" && (
                    <span
                      className="ms-2 gold-badge px-2 py-1 rounded"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {t("default")}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Options Cards */}
      <div className="row mb-5 g-4">
        {/* Full Payment Card */}
        <div className={paymentInfo?.show_deposit ? "col-md-4" : "col-md-6"}>
          <div
            className={`payment-card ${
              formData.paymentType === "full_payment" ? "selected" : ""
            }`}
            onClick={() => handlePaymentChange("full_payment")}
          >
            <div className="card-body p-4">
              <div className="d-flex align-items-start mb-3">
                <div className="payment-radio me-3">
                  <div
                    className={`radio-circle ${
                      formData.paymentType === "full_payment" ? "checked" : ""
                    }`}
                  ></div>
                </div>
                <div>
                  <h5 className="text-white mb-1">
                    {t("full")} {t("payment")}
                  </h5>
                </div>
              </div>

              <div className="mb-3">
                <div className="d-flex align-items-baseline">
                  <span className="gold-text fs-6 fw-light me-1">
                    {formData.currency === "USD" ? "$" : "C$"}
                  </span>
                  <h2 className="gold-text fw-bold display-5">
                    {paymentInfo?.total_amount}
                  </h2>
                  <span className="gold-text fs-6 fw-light ms-1">.00</span>
                  <span
                    className="text-light ms-2"
                    style={{ fontSize: "0.9rem", opacity: 0.7 }}
                  >
                    {formData.currency === "USD" ? "USD" : "CAD"}
                  </span>
                </div>
                <p
                  className="text-light mb-0"
                  style={{ opacity: 0.8, fontSize: "0.9rem" }}
                >
                  {t("one")} {t("time")} {t("payment")}
                </p>
              </div>

              <div className="benefits-list">
                <div className="benefit-item">
                  <span className="benefit-check gold-text">✓</span>
                  <span className="text-light">{t("peace_of_mind")}</span>
                </div>

                <div className="benefit-item">
                  <span className="benefit-check gold-text">✓</span>
                  <span className="text-light">
                    {t("no")} {t("additional")} {t("fees")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Partial Payment Card */}
        <div className={paymentInfo?.show_deposit ? "col-md-4" : "col-md-6"}>
          <div
            className={`payment-card ${
              formData.paymentType === "partial_payment" ? "selected" : ""
            }`}
            onClick={() => handlePaymentChange("partial_payment")}
          >
            <div className="card-body p-4">
              <div className="d-flex align-items-start mb-3">
                <div className="payment-radio me-3">
                  <div
                    className={`radio-circle ${
                      formData.paymentType === "partial_payment"
                        ? "checked"
                        : ""
                    }`}
                  ></div>
                </div>
                <div>
                  <h5 className="text-white mb-1">
                    {t("partial")} {t("payment")}
                  </h5>
                  <p
                    className="gold-text mb-0"
                    style={{ fontSize: "0.85rem", fontWeight: "500" }}
                  >
                    {t("flexible")} {t("payment")} {t("plan")}
                  </p>
                </div>
              </div>

              <div className="mb-3">
                <div className="d-flex align-items-baseline">
                  <span className="gold-text fs-6 fw-light me-1">
                    {formData.currency === "USD" ? "$" : "C$"}
                  </span>
                  <h2 className="gold-text fw-bold display-5">
                    {paymentInfo?.partial_payment?.split(".")?.[0]}
                  </h2>
                  <span className="gold-text fs-6 fw-light ms-1">
                    .{paymentInfo?.partial_payment?.split(".")?.[1]}
                  </span>
                  <span className="text-light ms-2" style={{ opacity: 0.7 }}>
                    × 3 {t("months")}
                  </span>
                </div>
                <p
                  className="text-light mb-0"
                  style={{ opacity: 0.8, fontSize: "0.9rem" }}
                >
                  {t("total")}: {formData.currency === "USD" ? "$" : "C$"}
                  {paymentInfo?.total_amount}.00
                </p>
              </div>

              <div className="benefits-list">
                <div className="benefit-item">
                  <span className="benefit-check gold-text">✓</span>
                  <span className="text-light">
                    {t("pay")} {t("in")} 3 {t("installments")}
                  </span>
                </div>

                <div className="benefit-item">
                  <span className="benefit-check gold-text">✓</span>
                  <span className="text-light">
                    {t("flexible")} {t("payment")} {t("schedule")}
                  </span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-check gold-text">✓</span>
                  <span className="text-light">{t("may_accrue_interest")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Deposit Card — only shown when total fee > $1000/rider */}
        {paymentInfo?.show_deposit && (
          <div className="col-md-4">
            <div
              className={`payment-card ${
                formData.paymentType === "deposit" ? "selected" : ""
              }`}
              onClick={() => handlePaymentChange("deposit")}
            >
              <div className="card-body p-4">
                <div className="d-flex align-items-start mb-3">
                  <div className="payment-radio me-3">
                    <div
                      className={`radio-circle ${
                        formData.paymentType === "deposit" ? "checked" : ""
                      }`}
                    ></div>
                  </div>
                  <div>
                    <h5 className="text-white mb-1">{t("deposit_reservation")}</h5>
                    <p
                      className="gold-text mb-0"
                      style={{ fontSize: "0.85rem", fontWeight: "500" }}
                    >
                      {t("deposit_per_rider")}
                    </p>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="d-flex align-items-baseline">
                    <span className="gold-text fs-6 fw-light me-1">
                      {formData.currency === "USD" ? "$" : "C$"}
                    </span>
                    <h2 className="gold-text fw-bold display-5">
                      {paymentInfo?.deposit_amount?.toLocaleString()}
                    </h2>
                    <span className="gold-text fs-6 fw-light ms-1">.00</span>
                    {paymentInfo?.rider_count > 1 && (
                      <span
                        className="text-light ms-2"
                        style={{ opacity: 0.7, fontSize: "0.85rem" }}
                      >
                        ({paymentInfo.rider_count} riders)
                      </span>
                    )}
                  </div>
                  <p
                    className="text-light mb-0"
                    style={{ opacity: 0.8, fontSize: "0.9rem" }}
                  >
                    {t("balance_due")}{" "}
                    {formData.currency === "USD" ? "$" : "C$"}
                    {(
                      parseFloat(paymentInfo?.total_amount || 0) -
                      (paymentInfo?.deposit_amount || 0)
                    ).toLocaleString()}
                    .00
                  </p>
                </div>

                <div className="benefits-list">
                  <div className="benefit-item">
                    <span className="benefit-check gold-text">✓</span>
                    <span className="text-light">{t("reserve_spot_now")}</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-check gold-text">✓</span>
                    <span className="text-light">{t("pay_balance_anytime")}</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-check gold-text">✓</span>
                    <span className="text-light">{t("full_or_3_part_balance")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pro-rate Info */}
      {paymentInfo?.pro_rate_info && (
        <div
          className="p-4 mb-4 rounded"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
          }}
        >
          <h6 className="text-white mb-3">{t("fee_breakdown_title")}</h6>

          {/* Summary row */}
          <div className="d-flex flex-wrap gap-4 mb-3">
            <div>
              <div
                className="text-light"
                style={{ opacity: 0.6, fontSize: "0.9rem" }}
              >
                {t("joining_month")}
              </div>
              <div className="text-white fw-semibold">
                {new Date(
                  2000,
                  paymentInfo.pro_rate_info.joinMonth - 1,
                ).toLocaleString("default", { month: "long" })}
              </div>
            </div>
            <div>
              <div
                className="text-light"
                style={{ opacity: 0.6, fontSize: "0.9rem" }}
              >
                {t("remaining_months")}
              </div>
              <div className="text-white fw-semibold">
                {paymentInfo.pro_rate_info.remainingMonths} /{" "}
                {paymentInfo.pro_rate_info.totalMonths}
              </div>
            </div>
            <div>
              <div
                className="text-light"
                style={{ opacity: 0.6, fontSize: "0.9rem" }}
              >
                {t("monthly_fee")}
              </div>
              <div className="gold-text fw-semibold">
                {formData.currency === "USD" ? "$" : "C$"}
                {(paymentInfo.fee_breakdown ?? []).reduce(
                  (sum, s) => sum + (s.pro_rate?.monthlyFee ?? 0),
                  0,
                )}
              </div>
            </div>
            <div>
              <div
                className="text-light"
                style={{ opacity: 0.6, fontSize: "0.9rem" }}
              >
                {t("total_due")}
              </div>
              <div className="gold-text fw-semibold">
                {formData.currency === "USD" ? "$" : "C$"}
                {paymentInfo.total_amount}.00
              </div>
            </div>
            <div>
              <div
                className="text-light"
                style={{ opacity: 0.6, fontSize: "0.9rem" }}
              >
                {t("annual_fee")}
              </div>
              <div className="gold-text fw-semibold">
                {formData.currency === "USD" ? "$" : "C$"}
                {(paymentInfo.fee_breakdown ?? [])
                  .reduce((sum, s) => sum + (s.annual_fee ?? 0), 0)
                  .toLocaleString()}
              </div>
            </div>
          </div>

          {/* Per-kid breakdown (only if more than 1 kid) */}
          {(paymentInfo.fee_breakdown ?? []).length > 1 && (
            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.1)",
                paddingTop: "12px",
              }}
            >
              <div
                className="text-light mb-2"
                style={{
                  opacity: 0.6,
                  fontSize: "0.85rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {t("per_student")}
              </div>
              <div className="d-flex flex-column gap-2">
                {paymentInfo.fee_breakdown.map((s, i) => (
                  <div
                    key={i}
                    className="d-flex flex-wrap gap-3 align-items-center"
                    style={{ fontSize: "0.95rem" }}
                  >
                    <span
                      className="text-white fw-semibold"
                      style={{ minWidth: "80px" }}
                    >
                      {s.student_name}
                    </span>
                    <span className="text-light" style={{ opacity: 0.7 }}>
                      {t("monthly_label")}{" "}
                      <span className="gold-text">
                        {formData.currency === "USD" ? "$" : "C$"}
                        {s.pro_rate?.monthlyFee ?? 0}
                      </span>
                    </span>
                    <span className="text-light" style={{ opacity: 0.7 }}>
                      {t("annual_label")}{" "}
                      <span className="gold-text">
                        {formData.currency === "USD" ? "$" : "C$"}
                        {(s.annual_fee ?? 0).toLocaleString()}
                      </span>
                    </span>
                    <span className="text-light" style={{ opacity: 0.7 }}>
                      {t("pro_rated_label")}{" "}
                      <span className="gold-text">
                        {formData.currency === "USD" ? "$" : "C$"}
                        {s.fee ?? 0}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Next Step Info */}
      <div
        className="info-box p-4 mb-4 rounded"
        style={{
          backgroundColor: "rgba(240, 178, 65, 0.1)",
          border: "1px solid rgba(240, 178, 65, 0.3)",
        }}
      >
        <div className="d-flex">
          <div className="me-3">
            <div className="info-icon">ℹ️</div>
          </div>
          <div>
            <h6 className="gold-text mb-2">
              {t("next")} {t("step")}
            </h6>
            <p
              className="text-light mb-0"
              style={{ opacity: 0.9, fontSize: "0.9rem" }}
            >
              {t("payment_gateway_note")}
            </p>
          </div>
        </div>
      </div>
      <div className="d-flex flex-column flex-md-row gap-3 justify-content-between mt-5">
        <button
          onClick={handleSubmit}
          type="button"
          className="btn btn-amber rounded-3 px-4 ms-auto"
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="spinner-border" role="status">
              <span className="sr-only p-0"></span>
            </div>
          ) : (
            t("pay")
          )}
        </button>
      </div>
      {/* Styles */}
      <style>{`
        .payment-card {
          background-color: rgba(255, 255, 255, 0.05);
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          transition: all 0.3s ease;
          height: 100%;
        }
        .payment-card:hover {
          border-color: rgba(240, 178, 65, 0.5);
          transform: translateY(-2px);
          cursor: pointer;
        }
        .payment-card.selected {
          border-color: #f0b241;
          background-color: rgba(240, 178, 65, 0.1);
          box-shadow: 0 5px 15px rgba(240, 178, 65, 0.2);
        }
        .radio-circle {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          position: relative;
          transition: all 0.2s ease;
        }
        .radio-circle.checked {
          border-color: #f0b241;
        }
        .radio-circle.checked::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 10px;
          height: 10px;
          background-color: #f0b241;
          border-radius: 50%;
        }
        .currency-circle {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          position: relative;
          transition: all 0.2s ease;
        }
        .currency-circle.checked {
          border-color: #f0b241;
        }
        .currency-circle.checked::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 8px;
          background-color: #f0b241;
          border-radius: 50%;
        }
        .currency-option:hover .currency-circle {
          border-color: rgba(240, 178, 65, 0.7);
        }
        .benefits-list {
          margin-top: 1.5rem;
        }
        .benefit-item {
          display: flex;
          align-items: center;
          margin-bottom: 0.75rem;
        }
        .benefit-check {
          color: #f0b241;
          font-weight: bold;
          margin-right: 10px;
          font-size: 1.1rem;
        }
        .info-icon {
          width: 36px;
          height: 36px;
          background-color: rgba(240, 178, 65, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #f0b241;
          font-size: 1rem;
        }
        .gold-text {
          color: #f0b241 !important;
        }
        .gold-badge {
          background-color: rgba(240, 178, 65, 0.2);
          color: #f0b241;
          border: 1px solid rgba(240, 178, 65, 0.3);
        }
        .gold-btn {
          background-color: #f0b241;
          color: #000;
          border: none;
          font-weight: 600;
        }
        .gold-btn:hover {
          background-color: #e0a235;
          transform: translateY(-1px);
        }
        .gold-outline-btn {
          background-color: transparent;
          color: #f0b241;
          border: 1px solid #f0b241;
        }
        .gold-outline-btn:hover {
          background-color: rgba(240, 178, 65, 0.1);
        }
      `}</style>
    </FromLayout>
  );
};

export default PaymentMethodPage;
